/**
 * WorkItemChipBody — the inner content of a work-item reference chip:
 * kind glyph, code, title, status segment.
 *
 * Lives in `src/components/` rather than beside the editor node because
 * TWO renderers draw this chip and they must not be allowed to drift:
 *
 *   - `src/editor/nodes/work-item-embed/WorkItemEmbedView.tsx` — the live
 *     ProseMirror NodeView (wiki doc, card description, comments, and the
 *     chat COMPOSER).
 *   - `src/features/chat/components/MessageRichText.tsx` — the sent chat
 *     message, which renders JSON rows without an editor.
 *
 * They had drifted, and the seam was visible to the user in the worst
 * possible place: typing `MOTE-16` in the chat composer produced the full
 * chip, and pressing Enter replaced it with a bare `MOTE-16: Title`. The
 * same reference rendered two ways either side of a keystroke, so sending
 * a message read as losing information.
 *
 * The caller owns the OUTER element — the NodeView needs an `<a>` wired to
 * its chip menu and `getPos`, the chat renderer needs a plain `<a>` (or a
 * `<span>` when there is nothing to link to). Both put
 * `.wiki-inline-chip .wiki-inline-chip--work-item` on it, which is where
 * the border, fill, sizing and baseline alignment come from.
 *
 * `title` and `statusName` are optional because the chat renderer paints a
 * chip before `useCardEmbed` resolves (and for a restricted / deleted
 * card, never resolves): a chip with a code but no title is a real state
 * on that surface, and it degrades to glyph + code rather than a spinner.
 */

import type { DeliverableKind } from '@/api/types-bridge'
import { KIND_COLOR_CLASSES, KindIcon } from '@/features/board/components/KindIcon'

export interface WorkItemChipBodyProps {
  /**
   * Null when the surface never learned the kind — a chat row whose card
   * has not resolved yet. The glyph is simply omitted; it is never
   * guessed, because a wrong glyph is worse than no glyph.
   */
  kind: DeliverableKind | null
  /** Shorthand (`MOTE-16`), or an id prefix when there is no shorthand. */
  code: string
  title?: string | null
  statusName?: string | null
}

export function WorkItemChipBody({ kind, code, title, statusName }: WorkItemChipBodyProps) {
  return (
    <>
      {/* Kind GLYPH, not a colour-only dot — the same glyph set that labels
          the kind on the board card, the card modal header and the
          Create-New menu, so an inline reference reads the same way as the
          thing it points at.

          `self-center` because an SVG is a replaced element with no text
          baseline: under the chip's `align-items: baseline` flexbox would
          align its BOTTOM EDGE to the line's baseline and sit it low.

          Both `text` and `darkText` are emitted because the chip's fill is
          `bg-elevated`, which is light on Default and dark on the two dark
          themes — the `*-700` tint alone sits near 2.6:1 there and fails
          AA (see `KIND_COLOR_CLASSES`). */}
      {kind ? (
        <span className="me-1 inline-flex shrink-0 self-center">
          <KindIcon
            kind={kind}
            size={13}
            className={`${KIND_COLOR_CLASSES[kind].text} ${KIND_COLOR_CLASSES[kind].darkText}`}
          />
        </span>
      ) : null}
      {/* Exactly ONE flex child may shrink — the title. Everything else
          carries `shrink-0`, and the reason shows up the moment a card has
          a long title: with every child shrinkable, flexbox squeezed the
          CODE until `MOTE-901` wrapped onto a second line inside the chip,
          which is nonsense (the code is the reference's identity, not
          prose). The status segment has the same problem one step later. */}
      <span className="wiki-inline-chip__ref shrink-0 whitespace-nowrap">{code}</span>
      {title ? (
        <>
          <span aria-hidden="true" className="wiki-inline-chip__ref me-1 shrink-0">
            :
          </span>
          {/* `min-w-0` is what makes `truncate` work at all: a flex child
              defaults to `min-width: auto`, which for nowrap content
              resolves to the FULL text width, so the title refused to
              shrink and the chip blew through its own `max-width: 100%`
              (measured at 912px inside a 779px editor, title sheared off
              mid-word at the border). `max-w-[28ch]` then stops a long
              title eating the whole line even when the room exists; the
              full title stays available via the anchor's `title`. */}
          <span className="wiki-inline-chip__ref me-1.5 min-w-0 max-w-[28ch] truncate">
            {title}
          </span>
        </>
      ) : null}
      {statusName ? (
        // Flush to the chip's trailing edge, filling its full inner height
        // — Jira renders the status as a SEGMENT of the chip, its edges
        // deliberately coinciding with the chip's border, rather than as a
        // badge floating inside it. `self-stretch` fills the cross-axis
        // (the container is `align-items: baseline`, so the default would
        // size it to its text); the chip drops its trailing padding so
        // there is no gap to the border; and the leading corners stay
        // square so the seam reads as a division while the trailing
        // corners follow the chip's own radius.
        <span className="wiki-inline-chip__status inline-flex shrink-0 items-center self-stretch whitespace-nowrap rounded-s-none rounded-e-[0.3rem] px-1.5">
          {statusName}
        </span>
      ) : null}
    </>
  )
}
